import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TIDS } from "@/constants/testIds";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";

export default function Register() {
  const { register, user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    artist_name: "",
    email: "",
    password: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) nav("/dashboard", { replace: true });
  }, [user]); // eslint-disable-line

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[88vh] grid grid-cols-1 lg:grid-cols-2">
      <Seo title="Create your account" description="Create a free E-Quantum account to access your royalty dashboard, split sheets and consultant." path="/register" noindex />
      <div className="flex items-center justify-center p-8 lg:p-16 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="tag mb-4">// Join</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mb-3">
            Create your account
          </h1>
          <p className="text-zinc-400 mb-8 text-sm">
            Already a member?{" "}
            <Link to="/login" className="text-[var(--primary)] hover:underline">
              Sign in
            </Link>
            .
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Full name
                </label>
                <input
                  required
                  data-testid={TIDS.registerName}
                  value={form.name}
                  onChange={update("name")}
                  className="input-gold"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Artist name
                </label>
                <input
                  data-testid={TIDS.registerArtistName}
                  value={form.artist_name}
                  onChange={update("artist_name")}
                  className="input-gold"
                  placeholder="ARIA"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Email
              </label>
              <input
                required
                type="email"
                data-testid={TIDS.registerEmail}
                value={form.email}
                onChange={update("email")}
                className="input-gold"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Password
              </label>
              <input
                required
                minLength={6}
                type="password"
                data-testid={TIDS.registerPassword}
                value={form.password}
                onChange={update("password")}
                className="input-gold"
                placeholder="At least 6 characters"
              />
            </div>

            {err && (
              <div
                data-testid={TIDS.registerError}
                className="border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid={TIDS.registerSubmit}
              className="btn-primary w-full justify-center"
            >
              {loading ? "Creating…" : "Claim my account"} <ArrowUpRight size={16} />
            </button>
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest mt-4">
              By signing up you agree to keep ownership of your masters & publishing.
            </p>
          </form>
        </div>
      </div>

      <div className="hidden lg:flex relative bg-[#0a0a0a] border-l border-white/10 p-12 flex-col justify-between dotted-grid order-1 lg:order-2">
        <div className="font-heading font-black text-2xl tracking-tighter">
          E·QUANTUM<span className="text-[var(--primary)]">.</span>
        </div>
        <div>
          <h2 className="font-heading font-black text-5xl tracking-tighter leading-[0.95] max-w-md">
            Take back your
            <br />
            <span className="text-[var(--primary)]">power.</span>
          </h2>
          <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
            Every dollar in royalties, every placement, every PRO — tracked in
            real time. We do the paperwork. You make the art.
          </p>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
          / sign up
        </div>
      </div>
    </div>
  );
}
